import { FlowButton } from 'components/ui/flow-button';
import { useEffect, useState } from 'react';
import {
  BookOpenCheck,
  Building2,
  FileArchive,
  FileSignature,
  LifeBuoy,
  Loader2,
  LockKeyhole,
  LogOut,
  ReceiptText,
  ShieldCheck,
} from 'lucide-react';
import NotFoundPage from 'pages/NotFoundPage';
import { loadCustomerIdentity } from 'lib/customerApi';
import { signInCustomer, signOutCustomer, watchCustomerAuth } from 'lib/customerAuth';
import { loadCustomerPortalShadow } from 'lib/customerPortalApi';
import { isCustomerPortalPreviewEnabled } from 'lib/customerPortalConfig';
import { CUSTOMER_PORTAL_W2_SECTIONS } from 'lib/customerPortalW2Model';

const SECTION_ICONS = {
  'orders-billing': ReceiptText,
  agreements: FileSignature,
  files: FileArchive,
  academy: BookOpenCheck,
  'company-tms': Building2,
  'communications-support': LifeBuoy,
  'legacy-claim': LockKeyhole,
};

function PortalReadinessCard({ section }) {
  const Icon = SECTION_ICONS[section.id] || ShieldCheck;
  const reasonId = `portal-section-${section.id}-reason`;
  return (
    <article
      aria-describedby={reasonId}
      className="border border-white/10 bg-white/[0.025] p-5 shadow-[0_22px_70px_rgba(0,0,0,0.24)]"
    >
      <div className="flex items-start justify-between gap-4">
        <span className="grid h-10 w-10 shrink-0 place-items-center border border-gold/25 bg-gold/[0.07] text-gold">
          <Icon className="h-4 w-4" aria-hidden="true" />
        </span>
        <span className="border border-gold/25 bg-gold/[0.06] px-2.5 py-1 font-body text-[10px] font-semibold uppercase tracking-[0.16em] text-gold">
          Contract held
        </span>
      </div>
      <h2 className="mt-5 font-display text-xl font-semibold text-white">{section.title}</h2>
      <p className="mt-3 font-body text-sm leading-6 text-white/58">{section.description}</p>
      <div id={reasonId} className="mt-5 border-t border-white/10 pt-4">
        <p className="font-body text-[10px] uppercase tracking-[0.18em] text-white/35">Earliest planned boundary</p>
        <p className="mt-1 font-body text-sm text-white/68">{section.earliestAvailability}</p>
        <p className="mt-2 font-body text-xs leading-5 text-gold/72">{section.gateIds.join(' · ')}</p>
      </div>
    </article>
  );
}

function CustomerPortalPreview() {
  const [authState, setAuthState] = useState({ status: 'loading', user: null, error: null });
  const [identityState, setIdentityState] = useState({ status: 'idle', customer: null, error: null });
  const [portalState, setPortalState] = useState({ status: 'idle', result: null, error: null });
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    let active = true;
    let unsubscribe = null;
    watchCustomerAuth((nextState) => {
      if (active) setAuthState({ error: null, ...nextState });
    }).then((stop) => {
      if (!active) stop();
      else unsubscribe = stop;
    }).catch((error) => {
      if (active) setAuthState({ status: 'error', user: null, error });
    });
    return () => {
      active = false;
      if (unsubscribe) unsubscribe();
    };
  }, []);

  useEffect(() => {
    let active = true;
    if (authState.status !== 'verified' || !authState.user) {
      setIdentityState({ status: 'idle', customer: null, error: null });
      setPortalState({ status: 'idle', result: null, error: null });
      return () => { active = false; };
    }

    setIdentityState({ status: 'loading', customer: null, error: null });
    setPortalState({ status: 'loading', result: null, error: null });
    loadCustomerIdentity(authState.user).then((result) => {
      if (active) setIdentityState({ status: 'ready', customer: result.customer, error: null });
    }).catch((error) => {
      if (active) setIdentityState({ status: 'error', customer: null, error });
    });
    loadCustomerPortalShadow(authState.user).then((result) => {
      if (active) setPortalState({ status: 'held', result, error: null });
    }).catch((error) => {
      if (active) setPortalState({ status: 'error', result: null, error });
    });
    return () => { active = false; };
  }, [authState.status, authState.user]);

  async function handleSubmit(event) {
    event.preventDefault();
    setSubmitting(true);
    setAuthState((current) => ({ ...current, error: null }));
    try {
      await signInCustomer(email, password);
      setPassword('');
    } catch (error) {
      setAuthState({ status: 'signed_out', user: null, error });
    } finally {
      setSubmitting(false);
    }
  }

  async function handleSignOut() {
    setSubmitting(true);
    try {
      await signOutCustomer();
    } finally {
      setSubmitting(false);
    }
  }

  const signedIn = authState.status === 'verified' && authState.user;
  const error = authState.error || identityState.error || portalState.error;

  return (
    <main className="min-h-screen bg-obsidian px-6 pb-20 pt-32 text-white">
      <section className="mx-auto max-w-6xl">
        <div className="border border-gold/20 bg-obsidian-surface p-7 shadow-[0_30px_100px_rgba(0,0,0,0.45)] md:p-10">
          <div className="flex flex-col gap-7 lg:flex-row lg:items-end lg:justify-between">
            <div className="max-w-3xl">
              <div className="inline-flex items-center gap-2 border border-gold/30 bg-gold/5 px-3 py-2 font-body text-xs uppercase tracking-[0.22em] text-gold">
                <ShieldCheck className="h-4 w-4" aria-hidden="true" />
                W2 private portal preview
              </div>
              <h1 className="mt-6 font-display text-4xl font-semibold leading-tight md:text-5xl">
                One account. Every purchased benefit—only after its owner confirms access.
              </h1>
              <p className="mt-5 font-body text-base leading-7 text-white/62">
                This isolated page validates the future customer portal structure. It does not display orders, grant files, sign agreements, create support tickets, change communication preferences, or launch TMS access.
              </p>
            </div>
            {signedIn && (
              <button
                type="button"
                onClick={handleSignOut}
                disabled={submitting}
                className="inline-flex min-h-12 items-center justify-center gap-2 border border-white/20 px-5 font-body text-sm text-white transition hover:border-gold/60 hover:text-gold disabled:opacity-50"
              >
                <LogOut className="h-4 w-4" aria-hidden="true" />
                Sign out
              </button>
            )}
          </div>

          {authState.status === 'loading' && (
            <div className="mt-10 flex min-h-48 items-center justify-center" role="status" aria-live="polite">
              <Loader2 className="h-7 w-7 animate-spin text-gold" aria-hidden="true" />
              <span className="sr-only">Loading customer sign-in</span>
            </div>
          )}

          {!signedIn && authState.status !== 'loading' && (
            <form onSubmit={handleSubmit} className="mt-10 max-w-xl border border-white/10 bg-black/20 p-6">
              <p className="font-body text-xs uppercase tracking-[0.2em] text-gold">Existing verified accounts only</p>
              <div className="mt-5 grid gap-4 sm:grid-cols-2">
                <label className="font-body text-sm text-white/72">
                  Email address
                  <input
                    type="email"
                    autoComplete="email"
                    value={email}
                    onChange={(event) => setEmail(event.target.value)}
                    required
                    className="mt-2 min-h-12 w-full border border-white/15 bg-black/30 px-4 text-white outline-none transition focus:border-gold/70"
                  />
                </label>
                <label className="font-body text-sm text-white/72">
                  Password
                  <input
                    type="password"
                    autoComplete="current-password"
                    value={password}
                    onChange={(event) => setPassword(event.target.value)}
                    required
                    className="mt-2 min-h-12 w-full border border-white/15 bg-black/30 px-4 text-white outline-none transition focus:border-gold/70"
                  />
                </label>
              </div>
              <FlowButton
                type="submit"
                disabled={submitting}
                className="mt-5 inline-flex min-h-12 items-center justify-center gap-2  px-6 font-body text-sm font-semibold  transition  disabled:opacity-50"
              >
                {submitting && <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" />}
                Verify account
              </FlowButton>
            </form>
          )}

          {error && (
            <p className="mt-6 border border-red-400/30 bg-red-400/10 px-4 py-3 font-body text-sm leading-6 text-red-100" role="alert">
              {error.message}
            </p>
          )}

          {signedIn && (
            <div className="mt-10">
              <section className="border border-white/10 bg-black/20 p-5" aria-live="polite">
                <p className="font-body text-[10px] uppercase tracking-[0.2em] text-gold">Verified account boundary</p>
                {identityState.status === 'loading' ? (
                  <p className="mt-3 flex items-center gap-2 font-body text-sm text-white/58" role="status">
                    <Loader2 className="h-4 w-4 animate-spin text-gold" aria-hidden="true" />
                    Loading the server-owned identity…
                  </p>
                ) : identityState.status === 'ready' && identityState.customer ? (
                  <div className="mt-3 flex flex-wrap gap-x-8 gap-y-2 font-body text-sm text-white/68">
                    <span>{identityState.customer.email}</span>
                    <span>Email verified</span>
                    <span>Account {identityState.customer.accountState}</span>
                  </div>
                ) : (
                  <p className="mt-3 font-body text-sm text-white/52">No customer profile is available.</p>
                )}
                {portalState.status === 'held' && (
                  <p className="mt-4 border-l-2 border-gold/50 pl-3 font-body text-sm leading-6 text-gold/80">
                    {portalState.result?.safeMessage} Gate: {portalState.result?.gateId}.
                  </p>
                )}
              </section>

              <div className="mt-6 grid gap-4 md:grid-cols-2 xl:grid-cols-3">
                {CUSTOMER_PORTAL_W2_SECTIONS.map((section) => (
                  <PortalReadinessCard key={section.id} section={section} />
                ))}
              </div>
            </div>
          )}

          <p className="mt-8 border-l-2 border-gold/50 pl-4 font-body text-xs leading-5 text-white/45">
            The direct TMS connection is pending for customer access, agreements, downloads, support and email preferences. No legacy fallback is enabled.
          </p>
        </div>
      </section>
    </main>
  );
}

export default function CustomerPortalPreviewPage() {
  if (!isCustomerPortalPreviewEnabled()) return <NotFoundPage />;
  return <CustomerPortalPreview />;
}
